import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../services/api";
import { useAuth } from "../../hooks/useAuth";
import { canViewAgencies } from "../../utils/agencyAccess";
import { useToast } from "../../hooks/useToast";
import styles from "./AbandonedIntakesPage.module.css";
import Card from "../../components/common/Card.jsx";
import Button from "../../components/common/Button.jsx";
import Input from "../../components/common/Input.jsx";
import ListLoadError from "../../components/admin/ListLoadError.jsx";
import AdminFiltersModal, {
  FiltersButton,
  FilterChips,
  buildFilterChips,
  countActiveFilters,
  removeFilterValue
} from "../../components/admin/AdminFiltersModal";
import { resolveDateRange } from "../../utils/dateRangePresets";

// Card 53713: "A, B and C" rather than "A and B and C" when more than one
// filter's option source failed. Mirrors the same local const in
// SupportTicketsPage/WelcomeCallsPage/AccountsReceivablePage -- deliberately
// NOT extracted to a shared util, which would widen this card's blast radius
// past its two declared page-local files.
const describeFilterList = (labels) =>
  labels.length <= 1
    ? labels.join("")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;

// Interaction types accepted by POST /admin/abandoned-intakes/:id/interactions
const INTERACTION_TYPES = [
  { value: "voicemail", label: "Voicemail", color: "#6366f1" },
  { value: "no_answer", label: "No Answer", color: "#dc2626" },
  { value: "email_sent", label: "Email Sent", color: "#3b82f6" },
  { value: "sms_sent", label: "SMS Sent", color: "#8b5cf6" },
  { value: "general", label: "General Note", color: "#6b7280" },
];

// Outbound types auto-mark Reached Out server-side; General Note never does
const PROVIDER_TYPE_OPTIONS = [
  { value: "DrCare", label: "DrCare" },
  { value: "Doctor Now", label: "Doctor Now" },
  { value: "Both", label: "Both networks" },
];

const REACHED_OUT_OPTIONS = [
  { value: "true", label: "Reached out" },
  { value: "false", label: "Not reached out" },
];

// URL query params owned by the Filters experience — the URL is the source
// of truth, so refresh/back/forward and profile round-trips restore state
const SELECT_FILTER_PARAMS = [
  "reached",
  "tier",
  "customTier",
  "agency",
  "network",
  "category",
  "interaction",
];

const readFiltersFromParams = (searchParams) => {
  const values = {};
  SELECT_FILTER_PARAMS.forEach((key) => {
    const value = searchParams.get(key);
    if (value) values[key] = value;
  });
  const preset = searchParams.get("preset");
  if (preset) {
    values.submitted = {
      preset: preset.toUpperCase(),
      start: searchParams.get("from") || "",
      end: searchParams.get("to") || ""
    };
  }
  return values;
};

const writeFiltersToParams = (values) => {
  const params = new URLSearchParams();
  SELECT_FILTER_PARAMS.forEach((key) => {
    if (values[key]) params.set(key, values[key]);
  });
  const submitted = values.submitted;
  if (submitted?.preset) {
    // an incomplete custom range applies nothing — don't persist it
    const isComplete = submitted.preset !== "CUSTOM" || (submitted.start && submitted.end);
    if (isComplete) {
      params.set("preset", submitted.preset);
      if (submitted.preset === "CUSTOM") {
        params.set("from", submitted.start);
        params.set("to", submitted.end);
      }
    }
  }
  return params;
};

const getInteractionLabel = (type) =>
  INTERACTION_TYPES.find((t) => t.value === type)?.label || type;

const getInteractionColor = (type) =>
  INTERACTION_TYPES.find((t) => t.value === type)?.color || "#6b7280";

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return "";
  const diffMin = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${Math.floor(diffHr / 24)}d ago`;
};

// Absolute date+time in ET — ClientDetailPage convention
const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit", timeZone: "America/New_York"
  }) + " EST";
};

const describeReachedOut = (attempt) => {
  if (!attempt.reachedOut) return "Mark as reached out";
  const when = attempt.reachedOutAt ? ` · ${formatDateTime(attempt.reachedOutAt)}` : "";
  return attempt.reachedOutBy?.name
    ? `Marked by ${attempt.reachedOutBy.name}${when}`
    : `Marked as reached out${when}`;
};

export default function AbandonedIntakesPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { toast } = useToast();
  const canEdit = hasPermission("abandoned_intakes.edit");

  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [gracePeriodHours, setGracePeriodHours] = useState(null);
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [standardTiers, setStandardTiers] = useState([]);
  const [customTiers, setCustomTiers] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [categories, setCategories] = useState([]);
  // Card 53713: which filter labels lost their options to a GENUINE failure.
  // A predicted 403 never lands here -- see loadFilterOptions.
  const [optionErrors, setOptionErrors] = useState([]);

  const [selected, setSelected] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [interactionsLoading, setInteractionsLoading] = useState(false);
  const [interactionsError, setInteractionsError] = useState("");
  const [interactionType, setInteractionType] = useState("voicemail");
  const [interactionNotes, setInteractionNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const debounceRef = useRef(null);
  const savingReachedRef = useRef(new Set());

  const filters = useMemo(() => readFiltersFromParams(searchParams), [searchParams]);

  const loadAttempts = useCallback(async (p = 1, s = search, f = filters) => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: p, limit: 20 });
      if (f.reached) params.append("reachedOut", f.reached);
      if (f.tier) params.append("membershipTier", f.tier);
      if (f.customTier) params.append("customTierId", f.customTier);
      if (f.agency) params.append("agencyId", f.agency);
      if (f.network) params.append("providerType", f.network);
      if (f.category) params.append("categoryId", f.category);
      if (f.interaction) params.append("interactionType", f.interaction);
      const range = resolveDateRange(f.submitted);
      if (range) {
        params.append("dateFrom", range.from);
        params.append("dateTo", range.to);
      }
      if (s) params.append("search", s);
      const data = await api.get(`/admin/abandoned-intakes?${params}`);
      setAttempts(data.attempts || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setGracePeriodHours(data.gracePeriodHours ?? null);
      setPage(p);
    } catch (err) {
      setAttempts([]);
      setError(err.message || "Failed to load abandoned intakes");
    } finally {
      setLoading(false);
    }
  }, [search, filters]);

  const loadInteractions = useCallback(async (attemptId) => {
    setInteractionsLoading(true);
    setInteractionsError("");
    try {
      const data = await api.get(`/admin/abandoned-intakes/${attemptId}/interactions`);
      setInteractions(data.interactions || []);
    } catch (err) {
      setInteractions([]);
      setInteractionsError(err.message || "Failed to load interaction history");
    } finally {
      setInteractionsLoading(false);
    }
  }, []);

  // Card 53713. The old shape swallowed EVERY failure into `setter([])` with no
  // affordance at all, and AdminFiltersModal DROPS a zero-option select outright
  // (its :264 predicate keeps a filter only when `(def.options?.length ?? 0) > 0`),
  // so four labelled controls vanished and the page said nothing -- a real outage
  // was indistinguishable from "this filter does not exist". The failure has no
  // empty-state string to grep for, which is exactly why the #53546-#53596
  // catch-block sweep passed over this site (see #53585's Learned).
  //
  // A PREDICTED 403 is NOT a load failure: these endpoints are gated by other
  // permission modules, so a denial keeps the original fail-soft silence
  // (card 49621). Only a genuine failure earns a voice. `error.status` is set on
  // every api.js error (services/api.js:255), which is what separates the arms.
  const loadFilterOptions = useCallback(() => {
    setOptionErrors([]);
    const noteFailure = (label, error) => {
      if (error?.status === 403) return;
      setOptionErrors((prev) => (prev.includes(label) ? prev : [...prev, label]));
    };
    const loadOptions = (path, pick, setter, label) =>
      api
        .get(path)
        .then((response) => setter(pick(response) || []))
        .catch((error) => {
          setter([]);
          noteFailure(label, error);
        });

    loadOptions("/admin/membership-tiers", (r) => (r.tiers || []).filter((t) => t.isActive), setStandardTiers, "Membership tier");
    loadOptions("/admin/custom-membership-tiers", (r) => (r.tiers || []).filter((t) => t.isActive), setCustomTiers, "Custom tier");
    // Card 49621: fail-soft still costs a guaranteed 403 for a role without
    // agencies.view. Skip the request instead of swallowing its denial.
    if (canViewAgencies(hasPermission)) {
      loadOptions("/admin/agencies", (r) => r.agencies, setAgencies, "Entity / Agency");
    } else {
      setAgencies([]);
    }
    loadOptions("/admin/categories", (r) => r.categories, setCategories, "Product type");
  }, [hasPermission]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // URL is the source of truth for filters — this also runs the initial load
  // and resets pagination to page 1 whenever filters change
  useEffect(() => {
    loadAttempts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Escape closes the detail panel
  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selected]);

  const filterDefs = useMemo(
    () => [
      // "Intake submitted" IS the list's primary date (effective_submitted_at)
      { key: "submitted", label: "Intake submitted", type: "dateRange" },
      { key: "reached", label: "Reached out", type: "select", placeholder: "Any", options: REACHED_OUT_OPTIONS },
      {
        key: "tier",
        label: "Membership tier",
        type: "select",
        placeholder: "All tiers",
        options: standardTiers.map((tier) => ({ value: tier.name, label: tier.name }))
      },
      {
        key: "customTier",
        label: "Custom tier",
        type: "select",
        placeholder: "All custom tiers",
        options: customTiers.map((tier) => ({ value: tier.id, label: tier.name }))
      },
      {
        key: "agency",
        label: "Entity / Agency",
        type: "select",
        placeholder: "All agencies",
        options: agencies.map((agency) => ({ value: agency.id || agency._id, label: agency.name }))
      },
      { key: "network", label: "Doctor network", type: "select", placeholder: "All networks", options: PROVIDER_TYPE_OPTIONS },
      {
        key: "category",
        label: "Product type",
        type: "select",
        placeholder: "All product types",
        options: categories.map((category) => ({ value: category.id || category._id, label: category.name }))
      },
      {
        key: "interaction",
        label: "Latest interaction",
        type: "select",
        placeholder: "Any interaction",
        options: INTERACTION_TYPES.map((t) => ({ value: t.value, label: t.label }))
      }
    ],
    [standardTiers, customTiers, agencies, categories]
  );

  const activeFilterCount = countActiveFilters(filters);
  const filterChips = useMemo(() => buildFilterChips(filterDefs, filters), [filterDefs, filters]);

  const applyFilters = (nextValues) => {
    setSearchParams(writeFiltersToParams(nextValues));
    setFiltersOpen(false);
  };

  const handleRemoveChip = (chip) => applyFilters(removeFilterValue(filters, chip));
  const handleResetFilters = () => applyFilters({});

  const handleSearchInput = (value) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => loadAttempts(1, value), 300);
  };

  // Optimistic toggle; the server response is authoritative (who/when for the
  // tooltip) and a failure reverts the row + toasts
  const toggleReachedOut = async (attempt) => {
    if (savingReachedRef.current.has(attempt.id)) return;
    savingReachedRef.current.add(attempt.id);
    const nextValue = !attempt.reachedOut;
    setAttempts((prev) => prev.map((a) => (a.id === attempt.id ? { ...a, reachedOut: nextValue } : a)));
    try {
      const data = await api.patch(`/admin/abandoned-intakes/${attempt.id}/reached-out`, { reachedOut: nextValue });
      if (data.attempt) {
        setAttempts((prev) => prev.map((a) => (a.id === attempt.id ? data.attempt : a)));
        setSelected((prev) => (prev?.id === attempt.id ? data.attempt : prev));
      }
    } catch (err) {
      setAttempts((prev) => prev.map((a) => (a.id === attempt.id ? attempt : a)));
      toast.error(err.message || "Failed to update reached-out status");
    } finally {
      savingReachedRef.current.delete(attempt.id);
    }
  };

  const openAttempt = (attempt) => {
    setSelected(attempt);
    setInteractionType("voicemail");
    setInteractionNotes("");
    loadInteractions(attempt.id);
  };

  const notesMissing = interactionType === "general" && !interactionNotes.trim();

  const submitInteraction = async () => {
    if (!selected || notesMissing) return;
    setSubmitting(true);
    try {
      const data = await api.post(`/admin/abandoned-intakes/${selected.id}/interactions`, {
        type: interactionType,
        notes: interactionNotes,
      });
      setInteractionNotes("");
      toast.success("Interaction logged");
      loadInteractions(selected.id);
      // Outbound types auto-mark Reached Out server-side — tick the row
      // without a refetch; the next list load stays authoritative
      const stampRow = (a) => {
        if (a.id !== selected.id) return a;
        const stamped = {
          ...a,
          interactions: {
            count: (a.interactions?.count ?? 0) + 1,
            latestType: interactionType,
            latestAt: data.interaction?.created_at ?? new Date().toISOString()
          }
        };
        return data.autoMarkedReachedOut
          ? { ...stamped, reachedOut: true, reachedOutAt: data.interaction?.created_at ?? null }
          : stamped;
      };
      setAttempts((prev) => prev.map(stampRow));
      setSelected((prev) => (prev ? stampRow(prev) : prev));
    } catch (err) {
      toast.error(err.message || "Failed to log interaction");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1>Abandoned Intakes</h1>
          <p>
            Members who submitted an intake but never completed checkout
            {gracePeriodHours ? ` — listed after a ${gracePeriodHours}-hour grace period` : ""}
          </p>
        </div>
      </div>

      {/* Search + Filters */}
      <Card className={styles.filters}>
        <div className={styles.filterRow}>
          <div className={styles.searchWrapper}>
            <Input
              placeholder="Search by name, member ID, email, or phone..."
              value={search}
              onChange={(e) => handleSearchInput(e.target.value)}
            />
          </div>
          <FiltersButton activeCount={activeFilterCount} onClick={() => setFiltersOpen(true)} />

          {optionErrors.length > 0 && (
            <div className={styles.optionsError} role="alert">
              <span>
                {`${describeFilterList(optionErrors)} ${
                  optionErrors.length > 1 ? "filters" : "filter"
                } could not be loaded`}
              </span>
              <button
                type="button"
                className={styles.optionsRetryBtn}
                onClick={loadFilterOptions}
              >
                Retry
              </button>
            </div>
          )}
        </div>
        <FilterChips chips={filterChips} onRemove={handleRemoveChip} />
      </Card>

      <AdminFiltersModal
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        filters={filterDefs}
        values={filters}
        onApply={applyFilters}
        onReset={handleResetFilters}
      />

      {/* Table */}
      <Card className={styles.tableCard}>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : error ? (
          <div className={styles.errorState}>
            <p>{error}</p>
            <Button size="small" variant="outline" onClick={() => loadAttempts(page, search)}>Retry</Button>
          </div>
        ) : attempts.length === 0 ? (
          <div className={styles.empty}>
            {search || activeFilterCount > 0
              ? "No intake attempts match your search or filters"
              : "No abandoned intakes right now"}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.reachedHeader}>Reached Out</th>
                  <th>Member Name</th>
                  <th>Member ID</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Attempted Product</th>
                  <th>Intake Submitted</th>
                  <th>Interactions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {attempts.map((a) => (
                  <tr key={a.id} className={styles.tableRow} onClick={() => openAttempt(a)}>
                    <td className={styles.reachedCell} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className={styles.reachedCheckbox}
                        checked={Boolean(a.reachedOut)}
                        disabled={!canEdit}
                        onChange={() => toggleReachedOut(a)}
                        aria-label={`Reached out — ${a.member?.fullName || "unknown member"}`}
                        title={canEdit ? describeReachedOut(a) : "Requires edit permission"}
                      />
                    </td>
                    <td>
                      <div className={styles.memberInfo}>
                        <div className={styles.avatar}>
                          {(a.member?.fullName || "?").charAt(0).toUpperCase()}
                        </div>
                        <span className={styles.name}>{a.member?.fullName || "Unknown member"}</span>
                      </div>
                    </td>
                    <td className={styles.memberId}>{a.member?.memberCode || "---"}</td>
                    <td className={styles.phone}>{a.member?.phone || "---"}</td>
                    <td className={styles.email}>{a.member?.email || "---"}</td>
                    <td>
                      <div className={styles.productCell}>
                        <span className={styles.productName}>{a.product?.name || "---"}</span>
                        {a.product && (
                          <span className={styles.productMeta}>
                            {[a.product.providerType, a.product.categoryName].filter(Boolean).join(" · ")}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className={styles.dateCell}>{formatDateTime(a.effectiveSubmittedAt)}</td>
                    <td>
                      <div className={styles.interactionsCell}>
                        <span className={styles.interactionCount}>{a.interactions?.count ?? 0}</span>
                        {a.interactions?.latestType && (
                          <span className={styles.interactionLatest}>
                            {getInteractionLabel(a.interactions.latestType)} · {formatRelativeTime(a.interactions.latestAt)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className={styles.actionBtns}>
                        <Button size="small" variant="outline" onClick={() => openAttempt(a)}>Log</Button>
                        {a.member && (
                          <Button size="small" variant="outline" onClick={() => navigate(`/admin/members/${a.member.id}`)}>
                            Profile
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!loading && !error && totalPages > 1 && (
          <div className={styles.pagination}>
            <Button disabled={page <= 1} onClick={() => loadAttempts(page - 1)} size="small">Previous</Button>
            <span>Page {page} of {totalPages}</span>
            <Button disabled={page >= totalPages} onClick={() => loadAttempts(page + 1)} size="small">Next</Button>
          </div>
        )}
      </Card>

      {/* Detail panel — attempt info + scoped interaction history + log form */}
      {selected && (
        <div className={styles.panelOverlay} onClick={() => setSelected(null)}>
          <div
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label="Abandoned intake details"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.panelHeader}>
              <div>
                <h3>{selected.member?.fullName || "Unknown member"}</h3>
                <p>
                  {[selected.member?.email, selected.member?.phone].filter(Boolean).join(" | ") ||
                    "Member record unavailable"}
                </p>
              </div>
              <div className={styles.panelHeaderActions}>
                {selected.member && (
                  <Button size="small" variant="outline" onClick={() => navigate(`/admin/members/${selected.member.id}`)}>
                    View Profile
                  </Button>
                )}
                <button className={styles.closeBtn} onClick={() => setSelected(null)} aria-label="Close details">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            </div>

            <div className={styles.attemptInfo}>
              <div className={styles.attemptInfoRow}>
                <span className={styles.attemptInfoLabel}>Attempted product</span>
                <span className={styles.attemptInfoValue}>{selected.product?.name || "---"}</span>
              </div>
              <div className={styles.attemptInfoRow}>
                <span className={styles.attemptInfoLabel}>Doctor network</span>
                <span className={styles.attemptInfoValue}>{selected.product?.providerType || "---"}</span>
              </div>
              <div className={styles.attemptInfoRow}>
                <span className={styles.attemptInfoLabel}>Product type</span>
                <span className={styles.attemptInfoValue}>{selected.product?.categoryName || "---"}</span>
              </div>
              <div className={styles.attemptInfoRow}>
                <span className={styles.attemptInfoLabel}>Membership tier</span>
                <span className={styles.attemptInfoValue}>{selected.member?.membershipTierName || "---"}</span>
              </div>
              <div className={styles.attemptInfoRow}>
                <span className={styles.attemptInfoLabel}>Intake submitted</span>
                <span className={styles.attemptInfoValue}>{formatDateTime(selected.effectiveSubmittedAt)}</span>
              </div>
              <div className={styles.attemptInfoRow}>
                <span className={styles.attemptInfoLabel}>Reached out</span>
                <span className={styles.attemptInfoValue}>
                  {selected.reachedOut ? describeReachedOut(selected) : "Not yet"}
                </span>
              </div>
              {selected.isConverted && (
                <div className={styles.convertedBadge}>Converted to an order</div>
              )}
            </div>

            <div className={styles.panelBody}>
              <h4>Interaction History</h4>
              {interactionsLoading ? (
                <div className={styles.loading}>Loading...</div>
              ) : interactionsError ? (
                <ListLoadError
                  message={interactionsError}
                  onRetry={() => loadInteractions(selected.id)}
                />
              ) : interactions.length === 0 ? (
                <div className={styles.emptySmall}>No interactions yet for this attempt</div>
              ) : (
                <div className={styles.interactionList}>
                  {interactions.map((i) => {
                    const type = i.interaction_type ?? i.interactionType;
                    const createdAt = i.created_at ?? i.createdAt;
                    return (
                      <div key={i.id} className={styles.interactionItem}>
                        <span
                          className={styles.typeBadge}
                          style={{
                            background: getInteractionColor(type) + "18",
                            color: getInteractionColor(type),
                            borderColor: getInteractionColor(type) + "40"
                          }}
                        >
                          {getInteractionLabel(type)}
                        </span>
                        {i.notes && <p className={styles.interactionNotes}>{i.notes}</p>}
                        <div className={styles.interactionMeta}>
                          <span>{i.created_by_admin_name ?? i.createdByAdminName}</span>
                          <span>{formatDateTime(createdAt)}</span>
                          <span className={styles.relTime}>{formatRelativeTime(createdAt)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {canEdit && selected.member && (
              <div className={styles.panelForm}>
                <h4>Log Interaction</h4>
                <label className={styles.formLabel} htmlFor="interaction-type">Type</label>
                <select
                  id="interaction-type"
                  className={styles.formSelect}
                  value={interactionType}
                  onChange={(e) => setInteractionType(e.target.value)}
                >
                  {INTERACTION_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <label className={styles.formLabel} htmlFor="interaction-notes">Notes</label>
                <textarea
                  id="interaction-notes"
                  className={styles.formTextarea}
                  placeholder={interactionType === "general" ? "Notes (required for General Note)..." : "Add notes..."}
                  value={interactionNotes}
                  onChange={(e) => setInteractionNotes(e.target.value)}
                  rows={3}
                />
                {notesMissing && (
                  <p className={styles.formHint}>Notes are required for a General Note.</p>
                )}
                <Button onClick={submitInteraction} disabled={submitting || notesMissing} fullWidth>
                  {submitting ? "Saving..." : "Log Interaction"}
                </Button>
              </div>
            )}
            {canEdit && !selected.member && (
              <div className={styles.panelForm}>
                <p className={styles.formHint}>
                  Interactions can’t be logged — the member record for this attempt no longer exists.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
